import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { Screen } from '@ghostscreen/contracts';
import { api } from '../../lib/api';

/**
 * Loads default playlist assignments for a set of screens.
 * Shared query keys with Manage / preview so cache stays coherent.
 */
export function useScreenAssignments(organisationId: string, screens: Screen[]) {
  const screenIds = screens.map((screen) => screen.id).join(',');

  const queries = useQueries({
    queries: screens.map((screen) => ({
      queryKey: ['screen-assignment', organisationId, screen.id],
      queryFn: () => api.getScreenAssignment(organisationId, screen.id),
    })),
  });

  const isPending = screens.length > 0 && queries.some((query) => query.isPending);
  const isError = queries.some((query) => query.isError);

  const resolved = queries
    .map((query) => {
      if (query.isPending) return 'pending';
      if (query.isError) return 'error';
      return query.data?.playlistId ?? '';
    })
    .join('|');

  const assignments = useMemo(() => {
    const map = new Map<string, string | null>();
    const ids = screenIds ? screenIds.split(',') : [];
    const values = resolved.split('|');
    ids.forEach((id, index) => {
      const value = values[index];
      if (!value || value === 'pending' || value === 'error') {
        map.set(id, null);
        return;
      }
      map.set(id, value);
    });
    return map;
  }, [screenIds, resolved]);

  return { assignments, isPending, isError };
}
